import { SparklesIcon } from "@storytell/bsky-icons";
import { type Component, Show } from "solid-js";
import { PricingModal } from "~/components/ui";
import { DebugElement, debugTheme } from "~/config/theme";
import { usePromptBarContext } from "../PromptBarContext";
import { useTooltipBehavior } from "../hooks";

/**
 * Credits display component with pill and detailed modal
 * Pure credits display without streaming state concerns
 */
export const CreditsDisplay: Component = () => {
  const context = usePromptBarContext();
  const { handleMouseLeave, handleMouseEnter } = useTooltipBehavior(context.credits.hideTooltip, 400);

  const handleClick = () => {
    context.credits.toggleTooltip();
  };

  return (
    <>
      <div class="relative">
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-300 bg-tile hover:bg-tile-hover cursor-pointer"
          onClick={handleClick}
        >
          <SparklesIcon class="w-4 h-4" />
          <span class="text-sm font-medium">{context.credits.totalCredits().toLocaleString()}</span>
          <span class="text-xs text-secondary-text">+ {context.credits.dailyCredits()}</span>
        </button>

        {/* Credits Modal */}
        <Show when={context.credits.showTooltip()}>
          <div class="relative">
            <div class={debugTheme(DebugElement.INVISIBLE_BRIDGE, "absolute inset-0 -m-4 -mt-16")} />
            <div
              class="absolute top-5 right-0 w-96 bg-panel border border-primary-border rounded-2xl shadow-xl z-50 overflow-hidden"
              onMouseLeave={handleMouseLeave}
              onMouseEnter={handleMouseEnter}
              style={{ "margin-bottom": "36px" }}
            >
              {/* Header */}
              <div class="px-6 py-5 border-b border-primary-border">
                <div class="flex items-center justify-between">
                  <div class="text-left">
                    <h3 class="text-xl font-bold">{context.credits.planType()} Plan</h3>
                    <p class="text-sm text-secondary-text mt-1">Your current credits and usage</p>
                  </div>
                  <button
                    type="button"
                    class="px-5 py-2.5 bg-brand text-on-brand rounded-full text-sm font-semibold hover:bg-brand-hover transition-colors shadow-sm"
                    onClick={context.credits.openPricingModal}
                  >
                    Upgrade
                  </button>
                </div>
              </div>

              {/* Content */}
              <div class="px-6 py-6 space-y-4">
                {/* Main Credits Row */}
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <SparklesIcon class="w-5 h-5" />
                    <span class="text-lg font-medium">Credits Available</span>
                  </div>
                  <span class="text-xl font-bold">{context.credits.totalCredits().toLocaleString()}</span>
                </div>

                {/* Daily Refresh Row */}
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <svg class="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span class="text-lg font-medium">Daily refresh credits</span>
                  </div>
                  <span class="text-xl font-bold">{context.credits.dailyCredits()}</span>
                </div>

                {/* Daily Refresh Subrow */}
                <div class="flex items-center justify-between pl-8">
                  <span class="text-sm text-secondary-text">
                    Adds {context.credits.dailyCredits()} at 12:00 AM Pacific
                  </span>
                </div>
              </div>

              {/* Footer */}
              <button
                type="button"
                class="w-full px-6 py-3 bg-panel hover:bg-panel-hover border-t border-primary-border transition-colors flex items-center justify-center gap-2 text-sm font-medium text-secondary-text hover:text-primary-text"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z"
                  />
                </svg>
                View detailed usage
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </Show>
      </div>

      {/* Pricing Modal */}
      <PricingModal isOpen={context.credits.showPricingModal()} onClose={context.credits.closePricingModal} />
    </>
  );
};
